/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { INavLinkGroup, INavStyleProps, INavStyles, Nav } from '@fluentui/react/lib/Nav';
import { Separator } from '@fluentui/react/lib/Separator';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { TelemetryFeatureTitle } from 'src/core/types/telemetryTypes';
import { PrebuiltDemosSection } from 'src/languageStudio/appShell/landingPage/components/PrebuiltDemosSection';
import { NextStepsGetStartedScreen } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsGetStartedScreen';
import { NextStepsPricingScreen } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsPricingScreen';
import { NextStepsResponsibleAiScreen } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsResponsibleAiScreen';
import { NextStepsRunCodeScreen } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsRunCodeScreen';
import { NextStepsSasSolutionScreen } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsSasSolutionScreen';
import { NextStepsSupportScreen } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsSupportScreen';
import { headerClassName } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { DemoId } from 'src/languageStudio/core/utils/demosUtils';

type NextStepsSteps = 'getStarted' | 'runCode' | 'pricing' | 'responsibleAi' | 'sasSolution' | 'support';
type NextStepsStepProps = {
    sdkLink?: string;
    featureName: TelemetryFeatureTitle;
    curlCommand: string;
    sampleCodeLink: string;
    isResultsEmpty: boolean;
    demosToDisplay: DemoId[];
    documentationLink: string;
    stepsToHide?: NextStepsSteps[];
    onRenderSteps?: (step: NextStepsSteps) => React.ReactNode;
};

const messages = defineMessages({
    pricing: { id: 'languageStudio.nextStepsStep.pricing', defaultMessage: 'See pricing' },
    runCode: { id: 'languageStudio.nextStepsStep.runCode', defaultMessage: 'Run the code' },
    nextSteps: { id: 'languageStudio.nextStepsStep.nextSteps', defaultMessage: 'Next steps' },
    support: { id: 'languageStudio.nextStepsStep.support', defaultMessage: 'Help and support' },
    getStarted: { id: 'languageStudio.nextStepsStep.getStarted', defaultMessage: 'Get started' },
    sasSolution: { id: 'languageStudio.nextStepsStep.sasSolution', defaultMessage: 'Consider SaaS solution' },
    responsibleAi: { id: 'languageStudio.nextStepsStep.responsibleAi', defaultMessage: 'Responsible use of AI' },
    notQuiteQuestion: { id: 'languageStudio.nextStepsStep.notQuiteQuestion', defaultMessage: 'Not quite what you’re looking for?' },
    tryOtherCapabilities: {
        id: 'languageStudio.nextStepsStep.tryOtherCapabilities',
        defaultMessage: 'Try these other prebuilt capabilities'
    }
});

const theme = getScopedTheme('DemoPage');

const containerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { border: '1px solid #DDDDDD', height: '420px' }
});
const screenContentStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { flex: 1, overflowY: 'auto', padding: '15px 28px' }
});
const supportNavStyles = classNamesFunction<INavStyleProps, INavStyles>()({
    root: { borderTop: '1px solid #EDEBE9' },
    linkText: { color: theme.defaultTextColor },
    groupContent: { margin: 0 }
});
export const NextStepsStep = (props: NextStepsStepProps) => {
    const {
        sdkLink,
        curlCommand,
        featureName,
        isResultsEmpty,
        demosToDisplay,
        sampleCodeLink,
        documentationLink,
        onRenderSteps = null,
        stepsToHide = ['sasSolution']
    } = props;
    const { localize } = useLocalization();

    const [selectedStep, setSelectedStep] = React.useState<NextStepsSteps>('getStarted');

    const handleSelectStep = (step: NextStepsSteps) => {
        logDemoButtonClick(featureName, 'Nextsteps', step);
        setSelectedStep(step);
    };

    const supportSteps: INavLinkGroup[] = [
        {
            links: [
                {
                    url: null,
                    icon: 'Info',
                    key: 'support',
                    target: '_blank',
                    name: localize(messages.support),
                    onClick: () => handleSelectStep('support')
                }
            ]
        }
    ];
    const nextSteps: INavLinkGroup[] = [
        {
            links: [
                {
                    url: null,
                    icon: 'Settings',
                    key: 'getStarted',
                    name: localize(messages.getStarted),
                    onClick: () => handleSelectStep('getStarted')
                },
                { url: null, icon: 'Code', key: 'runCode', name: localize(messages.runCode), onClick: () => handleSelectStep('runCode') },
                { url: null, icon: 'Money', key: 'pricing', name: localize(messages.pricing), onClick: () => handleSelectStep('pricing') },
                {
                    url: null,
                    icon: 'Shield',
                    key: 'responsibleAi',
                    name: localize(messages.responsibleAi),
                    onClick: () => handleSelectStep('responsibleAi')
                },
                {
                    url: null,
                    icon: 'TFVCLogo',
                    key: 'sasSolution',
                    name: localize(messages.sasSolution),
                    onClick: () => handleSelectStep('sasSolution')
                }
            ]
        }
    ];

    const getScreenContent = () => {
        const stepContent = onRenderSteps?.(selectedStep) ?? null;
        if (stepContent) {
            return stepContent;
        }

        switch (selectedStep) {
            case 'getStarted':
                return (
                    <NextStepsGetStartedScreen
                        tryoutName={featureName}
                        samplesLink={sampleCodeLink}
                        isResultsEmpty={isResultsEmpty}
                        documentationLink={documentationLink}
                    />
                );
            case 'runCode':
                return (
                    <NextStepsRunCodeScreen
                        sdkLink={sdkLink}
                        tryoutName={featureName}
                        curlCommand={curlCommand}
                        samplesLink={sampleCodeLink}
                        isResultsEmpty={isResultsEmpty}
                    />
                );
            case 'pricing':
                return <NextStepsPricingScreen demoTitle={featureName} isResultsEmpty={isResultsEmpty} />;
            case 'responsibleAi':
                return <NextStepsResponsibleAiScreen demoTitle={featureName} isResultsEmpty={isResultsEmpty} />;
            case 'support':
                return <NextStepsSupportScreen isResultsEmpty={isResultsEmpty} />;
            case 'sasSolution':
                return <NextStepsSasSolutionScreen isResultsEmpty={isResultsEmpty} />;
            default:
        }
    };

    return (
        <Stack tokens={{ childrenGap: 12 }}>
            <LuisText as="h2" className={headerClassName} variant="xLarge" fontWeight={600}>
                {localize(messages.nextSteps)}
            </LuisText>

            <Stack horizontal styles={containerStyles}>
                <Stack verticalAlign="space-between" verticalFill>
                    <Nav
                        selectedKey={selectedStep}
                        styles={{ linkText: { color: theme.defaultTextColor } }}
                        groups={nextSteps.map(stepsGroup => ({
                            links: stepsGroup.links.filter(step => stepsToHide.every(s => s !== step.key))
                        }))}
                    />
                    <Nav selectedKey={selectedStep} groups={supportSteps} styles={supportNavStyles} />
                </Stack>
                <Separator vertical styles={{ root: { padding: 0 } }} />
                <Stack styles={screenContentStyles}>{getScreenContent()}</Stack>
            </Stack>

            {demosToDisplay.length && [
                <LuisText as="h2" variant="xLarge" fontWeight={600} block styles={{ root: { paddingTop: 20 } }}>
                    {localize(messages.notQuiteQuestion)}
                </LuisText>,
                <LuisText>{localize(messages.tryOtherCapabilities)}</LuisText>,
                <PrebuiltDemosSection demoTitle={featureName} demosToDisplay={demosToDisplay} />
            ]}
        </Stack>
    );
};
